export const SUPPORTED_LANGUAGES = Object.freeze(["en", "ka"]);
export const LOCALES = Object.freeze({ en: "en-GB", ka: "ka-GE" });
let runtimeLanguage = "en";

const KEY_PATTERN = /^[a-z][A-Za-z0-9]*(?:[._-][A-Za-z0-9]+)+$/;
const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;
const REGEXP_SPECIAL_PATTERN = /[.*+?^${}()|[\]\\]/g;
const KA_WEEKDAYS_LONG = Object.freeze(["კვირა", "ორშაბათი", "სამშაბათი", "ოთხშაბათი", "ხუთშაბათი", "პარასკევი", "შაბათი"]);
const KA_WEEKDAYS_SHORT = Object.freeze(["კვი", "ორშ", "სამ", "ოთხ", "ხუთ", "პარ", "შაბ"]);
const KA_MONTHS_LONG = Object.freeze(["იანვარი", "თებერვალი", "მარტი", "აპრილი", "მაისი", "ივნისი", "ივლისი", "აგვისტო", "სექტემბერი", "ოქტომბერი", "ნოემბერი", "დეკემბერი"]);
const KA_MONTHS_SHORT = Object.freeze(["იან", "თებ", "მარ", "აპრ", "მაი", "ივნ", "ივლ", "აგვ", "სექ", "ოქტ", "ნოე", "დეკ"]);

export function normalizeLanguage(language) {
  return SUPPORTED_LANGUAGES.includes(language) ? language : "en";
}

export function localeFor(language) {
  return LOCALES[normalizeLanguage(language)];
}

export function setRuntimeLanguage(language) {
  runtimeLanguage = normalizeLanguage(language);
  return runtimeLanguage;
}

export function getRuntimeLanguage() {
  return runtimeLanguage;
}

export function extractPlaceholders(value, context = "message") {
  const placeholders = [];
  const seen = new Set();
  for (const match of String(value).matchAll(PLACEHOLDER_PATTERN)) {
    const name = match[1];
    if (seen.has(name)) throw new Error(`[i18n] Duplicate placeholder "${name}" in ${context}`);
    seen.add(name);
    placeholders.push(name);
  }
  return placeholders.sort();
}

export function defineCatalog(name, entries) {
  if (!name || !entries || Array.isArray(entries) || typeof entries !== "object") {
    throw new Error("[i18n] defineCatalog requires a name and an entries object");
  }
  const catalog = {};
  Object.entries(entries).forEach(([key, message]) => {
    if (!KEY_PATTERN.test(key)) throw new Error(`[i18n] Invalid stable key "${key}" in ${name}`);
    if (!message || Array.isArray(message) || typeof message !== "object") {
      throw new Error(`[i18n] ${name}.${key} must provide { en, ka }`);
    }
    if (Object.keys(message).sort().join(",") !== "en,ka") {
      throw new Error(`[i18n] ${name}.${key} must contain exactly en and ka`);
    }
    if (typeof message.en !== "string" || !message.en.trim() || typeof message.ka !== "string" || !message.ka.trim()) {
      throw new Error(`[i18n] ${name}.${key} has an empty translation`);
    }
    const en = extractPlaceholders(message.en, `${name}.${key}.en`);
    const ka = extractPlaceholders(message.ka, `${name}.${key}.ka`);
    if (en.length !== ka.length || en.some((value, index) => value !== ka[index])) {
      throw new Error(`[i18n] Placeholder mismatch in ${name}.${key}: en=[${en}] ka=[${ka}]`);
    }
    catalog[key] = Object.freeze({ en: message.en, ka: message.ka });
  });
  return Object.freeze({ name, entries: Object.freeze(catalog) });
}

export function createCatalogRegistry(catalogs) {
  const messages = {};
  const sourceKeys = {};
  const sourcePatterns = [];
  catalogs.forEach((catalog) => {
    Object.entries(catalog.entries).forEach(([key, message]) => {
      if (messages[key]) throw new Error(`[i18n] Duplicate catalog key "${key}" in ${catalog.name}`);
      messages[key] = message;
      const names = extractPlaceholders(message.en, `${catalog.name}.${key}.en`);
      if (!names.length) {
        if (!sourceKeys[message.en]) sourceKeys[message.en] = key;
        return;
      }
      let lastIndex = 0;
      let pattern = "^";
      const orderedNames = [];
      for (const match of message.en.matchAll(PLACEHOLDER_PATTERN)) {
        pattern += message.en.slice(lastIndex, match.index).replace(REGEXP_SPECIAL_PATTERN, "\\$&");
        pattern += "(.+?)";
        orderedNames.push(match[1]);
        lastIndex = match.index + match[0].length;
      }
      pattern += `${message.en.slice(lastIndex).replace(REGEXP_SPECIAL_PATTERN, "\\$&")}$`;
      const specificity = message.en.replace(PLACEHOLDER_PATTERN, "").length;
      sourcePatterns.push(Object.freeze({ key, names: Object.freeze(orderedNames), regex: new RegExp(pattern), specificity }));
    });
  });
  sourcePatterns.sort((a, b) => b.specificity - a.specificity);
  return Object.freeze({
    messages: Object.freeze(messages),
    sourceKeys: Object.freeze(sourceKeys),
    sourcePatterns: Object.freeze(sourcePatterns),
  });
}

export function interpolate(value, params = {}) {
  return String(value).replace(PLACEHOLDER_PATTERN, (_, key) => (
    params[key] === undefined || params[key] === null ? "" : String(params[key])
  ));
}

export function formatNumberValue(value, language, options) {
  const numeric = Number(value ?? 0);
  return new Intl.NumberFormat(localeFor(language), options).format(Number.isFinite(numeric) ? numeric : 0);
}

export function formatDateValue(value, language, options) {
  const date = value instanceof Date ? value : new Date(value);
  if (normalizeLanguage(language) === "ka") return formatGeorgianDate(date, options);
  return new Intl.DateTimeFormat(localeFor(language), options).format(date);
}

// Some browser builds ship without Georgian ICU date data and silently fall
// back to English month/weekday names. Keep the product deterministic instead
// of letting the host browser decide whether Georgian is complete.
function formatGeorgianDate(date, options = {}) {
  if (Number.isNaN(date.getTime())) return "";
  const config = options || {};
  const pad = (number) => String(number).padStart(2, "0");
  const hasTime = Boolean(config.hour || config.minute || config.second || config.timeStyle);
  const hasDate = Boolean(config.weekday || config.day || config.month || config.year || config.dateStyle);
  const time = [pad(date.getHours()), pad(date.getMinutes())];
  if (config.second) time.push(pad(date.getSeconds()));
  const timeText = time.join(":");
  if (hasTime && !hasDate) return timeText;

  const weekday = config.weekday === "long"
    ? KA_WEEKDAYS_LONG[date.getDay()]
    : config.weekday
      ? KA_WEEKDAYS_SHORT[date.getDay()]
      : "";
  const day = config.day === "2-digit" ? pad(date.getDate()) : config.day ? String(date.getDate()) : "";
  const month = config.month === "long"
    ? KA_MONTHS_LONG[date.getMonth()]
    : config.month === "short"
      ? KA_MONTHS_SHORT[date.getMonth()]
      : config.month === "2-digit"
        ? pad(date.getMonth() + 1)
        : config.month
          ? String(date.getMonth() + 1)
          : "";
  const year = config.year === "2-digit" ? String(date.getFullYear()).slice(-2) : config.year ? String(date.getFullYear()) : "";

  let dateText = "";
  if (config.dateStyle && !config.weekday && !config.day && !config.month && !config.year) {
    dateText = `${date.getDate()} ${KA_MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`;
  } else if (day || month || year) {
    dateText = [day, month, year].filter(Boolean).join(" ");
  }
  if (weekday) dateText = dateText ? `${weekday}, ${dateText}` : weekday;
  if (!dateText && !hasTime) dateText = `${date.getDate()} ${KA_MONTHS_LONG[date.getMonth()]} ${date.getFullYear()}`;
  return hasTime ? `${dateText} · ${timeText}` : dateText;
}

export function formatMoneyValue(value, language) {
  if (value === null || value === undefined || value === "") return "—";
  return `₾${formatNumberValue(value, language)}`;
}

export function formatRelativeTimeValue(value, language, now = Date.now()) {
  const diffMinutes = (new Date(value).getTime() - Number(now)) / 60000;
  const absoluteMinutes = Math.abs(diffMinutes);
  let unit = "minute";
  let amount = Math.round(diffMinutes);
  if (absoluteMinutes < 1) amount = 0;
  else if (absoluteMinutes >= 60 && absoluteMinutes < 1440) {
    unit = "hour";
    amount = Math.round(diffMinutes / 60);
  } else if (absoluteMinutes >= 1440) {
    unit = "day";
    amount = Math.round(diffMinutes / 1440);
  }
  return new Intl.RelativeTimeFormat(localeFor(language), { numeric: "auto", style: "short" }).format(amount, unit);
}
